/**
 * Behavior for sections/newsletter-popup.liquid, ported from the approved static demo
 * (assets/global/newsletter-popup.js in the source review site v2026.07.15.03).
 *
 * Kept 1:1 from the demo: the 15s-after-first-visit open delay, the 7-day dismiss gate and
 * permanent post-subscribe gate (both localStorage-backed), the focus trap, and Escape/
 * click-outside close behavior.
 *
 * Changed from the demo (see the header comment in sections/newsletter-popup.liquid for the
 * full reasoning): the demo's createMarkup() built the whole modal via innerHTML and its form
 * submit handler was a `event.preventDefault()` that faked a success state client-side without
 * ever sending a request. The section now server-renders the modal (so it can carry real
 * section settings and a real `{% form 'customer' %}`), so this file no longer creates markup
 * or intercepts submit — it only finds the existing root and manages open/close state. On a
 * real page load after a real submission, the root carries `data-of-newsletter-posted` /
 * `data-of-newsletter-has-errors` (set server-side from `form.posted_successfully?` /
 * `form.errors`); this script reads those to force the popup open in the matching state
 * immediately, bypassing the normal 15s delay and the dismissed/subscribed gate, so the
 * visitor sees the result of the submission they just made instead of the popup staying
 * closed until its next scheduled appearance.
 *
 * Origin scoping: `form.posted_successfully?`/`form.errors` are NOT scoped to which literal
 * `<form>` on the page was submitted — they're true for every `{% form 'customer' %}` on the
 * rendered page after ANY customer-form post, including sections/footer.liquid's own separate
 * newsletter form (same form type + contact[tags]=newsletter). So `data-of-newsletter-posted`
 * alone can't tell "the visitor submitted the popup" from "the visitor submitted the footer" —
 * without a further check this modal would force itself open after a footer-only submission
 * nobody asked to see. A `submit` listener on this popup's own form stamps a sessionStorage flag
 * right before the native submit navigates away; on the next load, that flag (read once, then
 * cleared) is the only thing allowed to trigger openPopup() for the success/error views. A
 * footer-driven `wasPosted` still marks the visitor subscribed (so the entry popup won't nag them
 * later) — it just doesn't pop the modal open on top of the footer's own inline confirmation.
 *
 * Analytics (project-context/tracking-plan-master-v1.md, Layer B — this outcome can only be
 * known after the redirect-back page load, so it can't be expressed as a data-analytics-event
 * click attribute the way most Layer B events are): dispatches generate_lead / generate_lead_failed
 * once per real submission, with source=popup|footer read from the same submittedFromPopup flag
 * this file already uses to decide whether to pop the modal open.
 *
 * Visibility gate: if the 15s timer elapses while the tab is backgrounded, the popup waits for
 * `visibilitychange` rather than opening immediately, so it doesn't pop up while the visitor is
 * looking at a different tab/app. That wait used to be unbounded — a tab left backgrounded for
 * the rest of the session would hold the listener forever and the popup would never open, which
 * reads indistinguishably from "the popup is broken." `VISIBILITY_WAIT_CAP_MS` bounds it: if the
 * tab hasn't come back to foreground within that extra window, the listener is torn down and this
 * page load simply forgoes its auto-open (neither `dismissed` nor `subscribed` gets set, so the
 * next page load evaluates fresh).
 */
import { dispatch } from '@theme/analytics-bridge';

(function () {
  "use strict";

  if (window.__ofNewsletterPopupReady) return;
  window.__ofNewsletterPopupReady = true;

  var SUBSCRIBED_KEY = "ofhunger_newsletter_subscribed_v1";
  var DISMISSED_KEY = "ofhunger_newsletter_dismissed_v1";
  var SUBMIT_ORIGIN_KEY = "ofhunger_newsletter_popup_submit_v1";
  var DISMISS_FOR_MS = 7 * 24 * 60 * 60 * 1000;
  var OPEN_AFTER_MS = 15000;
  var VISIBILITY_WAIT_CAP_MS = 45000;
  var lastFocusedElement = null;
  var closeTimer = null;
  var isOpen = false;

  function readStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // The popup still works when storage is unavailable; only persistence is skipped.
    }
  }

  function readAndClearSubmitOrigin() {
    try {
      var value = window.sessionStorage.getItem(SUBMIT_ORIGIN_KEY);
      window.sessionStorage.removeItem(SUBMIT_ORIGIN_KEY);
      return value === "1";
    } catch (error) {
      return false;
    }
  }

  function writeSubmitOrigin() {
    try {
      window.sessionStorage.setItem(SUBMIT_ORIGIN_KEY, "1");
    } catch (error) {
      // If sessionStorage is unavailable, a footer-driven post can no longer be told apart from
      // a popup-driven one; the safer failure mode is staying closed, not popping open uninvited.
    }
  }

  function shouldOpen() {
    if (readStorage(SUBSCRIBED_KEY) === "true") return false;

    var dismissedAt = Number(readStorage(DISMISSED_KEY));
    return !dismissedAt || Date.now() - dismissedAt >= DISMISS_FOR_MS;
  }

  function getFocusableElements(root) {
    return Array.prototype.slice.call(root.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
    )).filter(function (element) {
      return !element.hidden && element.offsetParent !== null;
    });
  }

  function init() {
    var root = document.querySelector("[data-of-newsletter-root]");
    if (!root || root.dataset.ofNewsletterInit === "true") return;
    root.dataset.ofNewsletterInit = "true";

    var closeButton = root.querySelector("[data-of-newsletter-close]");
    var successTitle = root.querySelector("[data-of-newsletter-success-title]");
    var emailInput = root.querySelector(".of-newsletter-input");
    var popupForm = root.querySelector(".of-newsletter-form");
    var wasPosted = root.dataset.ofNewsletterPosted === "true";
    var hasErrors = root.dataset.ofNewsletterHasErrors === "true";
    var submittedFromPopup = readAndClearSubmitOrigin();

    if (popupForm) {
      popupForm.addEventListener("submit", writeSubmitOrigin);
    }

    function openPopup(focusTarget) {
      if (isOpen) return;

      lastFocusedElement = document.activeElement;
      root.hidden = false;
      document.body.classList.add("of-newsletter-open");
      isOpen = true;

      window.requestAnimationFrame(function () {
        root.classList.add("is-visible");
        window.setTimeout(function () {
          if (isOpen && focusTarget) focusTarget.focus({ preventScroll: true });
        }, 500);
      });
    }

    function closePopup(rememberDismissal) {
      if (!isOpen) return;
      if (rememberDismissal) writeStorage(DISMISSED_KEY, String(Date.now()));
      var focusTarget = lastFocusedElement;

      root.classList.remove("is-visible");
      document.body.classList.remove("of-newsletter-open");
      isOpen = false;
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(function () {
        root.hidden = true;
        if (focusTarget && typeof focusTarget.focus === "function") {
          focusTarget.focus({ preventScroll: true });
        }
      }, 480);
    }

    closeButton.addEventListener("click", function () {
      closePopup(true);
    });

    root.addEventListener("click", function (event) {
      if (event.target === root) closePopup(true);
    });

    document.addEventListener("keydown", function (event) {
      if (!isOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        closePopup(true);
        return;
      }

      if (event.key !== "Tab") return;
      var focusable = getFocusableElements(root);
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (!root.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (focusable.length === 1) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (wasPosted) {
      writeStorage(SUBSCRIBED_KEY, "true");
      dispatch("generate_lead", { form_location: submittedFromPopup ? "popup" : "footer", method: "email" });
      if (submittedFromPopup) openPopup(successTitle);
      return;
    }

    if (hasErrors) {
      dispatch("generate_lead_failed", { form_location: submittedFromPopup ? "popup" : "footer", method: "email" });
      if (submittedFromPopup) openPopup(emailInput);
      return;
    }

    if (!shouldOpen()) return;

    window.setTimeout(function () {
      if (document.visibilityState === "hidden") {
        var capTimer = window.setTimeout(function () {
          document.removeEventListener("visibilitychange", showWhenVisible);
        }, VISIBILITY_WAIT_CAP_MS);

        var showWhenVisible = function () {
          if (document.visibilityState !== "visible") return;
          window.clearTimeout(capTimer);
          document.removeEventListener("visibilitychange", showWhenVisible);
          openPopup(emailInput);
        };

        document.addEventListener("visibilitychange", showWhenVisible);
        return;
      }
      openPopup(emailInput);
    }, OPEN_AFTER_MS);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
